import { forwardRef } from 'react';
import { Spinner } from './Spinner.js';

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** True while a server call is in flight — disables the button and shows a spinner. */
  loading?: boolean;
  /** Hide the children while loading (e.g. for icon-only buttons). Default: keep label visible. */
  hideLabelWhileLoading?: boolean;
}

/**
 * Drop-in replacement for `<button>` that automatically:
 *   - injects a left-aligned spinner when `loading` is true,
 *   - disables itself while loading or when `disabled` is true,
 *   - inherits text color so the spinner matches the button.
 *
 * Pass `loading={mutation.isPending}` (or any boolean) — the rest is identical
 * to a normal button.
 */
export const SpinnerButton = forwardRef<HTMLButtonElement, Props>(function SpinnerButton(
  {
    loading = false,
    hideLabelWhileLoading = false,
    disabled,
    className,
    children,
    onClick,
    type,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={isDisabled}
      onClick={onClick}
      className={`relative inline-flex items-center justify-center gap-2 ${className ?? ''}`}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Spinner />}
      {loading && hideLabelWhileLoading ? (
        <span className="opacity-0">{children}</span>
      ) : (
        children
      )}
    </button>
  );
});
